import _ from "lodash";
import moment from "moment";
import languageModel from "../../models/language.model";
import { formats as FORMATS } from '../../config/constants';
import { easepick, AmpPlugin, RangePlugin, LockPlugin } from '@easepick/bundle';
import 'CssFolder/components/custom-easepick.scss';

/**
 * This is a date picker component.
 */
export default function DatePickerComponent() {

  let picker;
  let pickerId = 'date-picker';
  let required;
  let readonly;
  let isRange = false;
  let date = '';
  let dateStart = '';
  let dateEnd = '';
  let hasResetButton = false;
  let autoApply = true;
  let firstDay = 0; // 0 - Sun, 1 - Mon, ...
  let format = FORMATS.dateFormat;
  let grid = 1;
  let calendars = 1;
  let onselect = (e) => { };
  let onclear = (e) => { };

  // AmpPlugin Options
  let yearsDropdown = true;
  let monthsDropdown = true;

  // RangePlugin Options - start
  let delimiter = '   ~   ';
  // RangePlugin Options - end

  // LockPlugin Options - start
  let minDate = moment().date(1).month(0).subtract(100, 'years').format(format);
  let maxDate = moment().date(31).month(11).add(100, 'years').format(format);
  let minDays = null;
  let maxDays = null;
  let inseparable = false;
  // LockPlugin Options - end

  const onchange = (event) => {
    const value = event.target.value;

    if (isRange) {
      let [start, end] = value.split(delimiter.trim());
      start = moment(start, FORMATS.dateFormat);
      end = moment(end, FORMATS.dateFormat);

      if (start.isValid() && end.isValid()) {
        start = getBoundedDate(start);
        end = getBoundedDate(end);

        if (start > end) {
          dateStart = end.format(FORMATS.dateFormat);
          dateEnd = end.format(FORMATS.dateFormat);
        } else {
          dateStart = start.format(FORMATS.dateFormat);
          dateEnd = end.format(FORMATS.dateFormat);
        }
      }

      picker.setStartDate(dateStart);
      picker.setEndDate(dateEnd);

      validate();
      onselect({ detail: { start: dateStart, end: dateEnd } });
    } else {

      let newDate = moment(value);
      if (newDate.isValid()) {
        newDate = getBoundedDate(newDate);
        date = newDate.format(FORMATS.dateFormat);
      }

      picker.setDate(date);
      onselect({ detail: { date } });
    }
  }

  const getBoundedDate = (date) => {
    const momentMinDate = moment(minDate, FORMATS.dateFormat);
    const momentMaxDate = moment(maxDate, FORMATS.dateFormat);

    if (date < momentMinDate) {
      return momentMinDate;
    } else if (date > momentMaxDate) {
      return momentMaxDate;
    } else {
      return date;
    }
  }

  // To generate unique id for the component
  const generateID = () => {
    return '_' + Math.random().toString(36).substring(2, 11);
  }

  const initPickerElement = () => {
    const dateRangePickerEl = document.getElementById(pickerId);

    if (!dateRangePickerEl) {
      return
    }

    picker = new easepick.create({
      element: dateRangePickerEl,
      css: [
        'static/css/easepick.css',
        'static/css/easepick-custom.css',
      ],
      autoApply: autoApply,
      zIndex: 1000,
      readonly: readonly,
      format: format,
      firstDay: firstDay,
      grid: grid,
      calendars: calendars,
      locale: {
        apply: languageModel.getText("LT00000200", "Apply"),
        cancel: languageModel.getText("LT00000057", "Cancel")
      },
      AmpPlugin: {
        dropdown: {
          minYear: moment(minDate, format).format('YYYY'),
          maxYear: moment(maxDate, format).format('YYYY'),
          years: yearsDropdown,
          months: monthsDropdown
        },
        resetButton: hasResetButton,
        darkMode: false
      },
      RangePlugin: {
        delimiter: delimiter
      },
      LockPlugin: {
        inseparable: inseparable,
        minDate: minDate,
        maxDate: maxDate,
      },
      plugins: [
        AmpPlugin,
        RangePlugin,
        LockPlugin
      ],
      setup(picker) {
        const lockPlugin = picker.PluginManager.getInstance('LockPlugin');
        lockPlugin.options.minDays = minDays;
        lockPlugin.options.maxDays = maxDays;

        picker.on('select', e => {
          if (isRange) {
            dateStart = moment(e.detail.start).format(format);
            dateEnd = moment(e.detail.end).format(format);
          } else {
            date = moment(e.detail.date).format(format);
          }

          validate();
          m.redraw();
          onselect(e);
        });
        picker.on('clear', e => {
          onclear(e);
        });
      },
    });

    // Temporary workaround - plugins are added by default, and to be removed when not needed
    if (!isRange) {
      picker.PluginManager.removeInstance('RangePlugin');
      picker.setDate(date)
    }

    // Easepick BUG? Not working - https://easepick.com/packages/core.html#pluginmanager
    // if (isRange) {
    //   // add RangePlugin to the picker
    //   const rangePlugin = picker.PluginManager.addInstance('RangePlugin');
    //   // change plugin option
    //   rangePlugin.options.delimiter = delimiter;
    // }
  }

  const validate = function () {
    let isEmpty = true;
    const dateRangePickerEl = document.getElementById(pickerId);

    dateRangePickerEl.setCustomValidity('');
    if (maxDays && Math.abs(moment(dateStart, FORMATS.dateFormat).diff(moment(dateEnd, FORMATS.dateFormat), 'days')) > maxDays - 1) {
      dateRangePickerEl.classList.add('is-invalid-custom');
      dateRangePickerEl.setCustomValidity('Out of range');
      dateRangePickerEl.reportValidity();
    }

    if (required && dateRangePickerEl.value.length < 1) {
      isEmpty = true;
      dateRangePickerEl.classList.add('is-invalid-custom');
    } else {
      isEmpty = false;
      dateRangePickerEl.classList.remove('is-invalid-custom');
    }

    return !isEmpty;
  }

  const isDateEmpty = (date, dateStart, dateEnd) => {
    if (isRange) {
      return _.isEmpty(dateStart) || _.isEmpty(dateEnd);
    } else {
      return _.isEmpty(date);
    }
  }

  const renderLabel = (date, dateStart, dateEnd) => {
    if (isRange) {
      if (!_.isEmpty(dateStart) && !_.isEmpty(dateEnd)) {
        return dateStart + delimiter + dateEnd;
      }
    } else if (!_.isEmpty(date)) {
      return date;
    }

    return '';
  }

  const getPlaceholder = () => {
    if (isRange) {
      return `${languageModel.getText('LT00001039', 'From Date')} ${delimiter} ${languageModel.getText('LT00001041', 'To Date')}`;
    } else {
      return languageModel.getText('LT00000132', 'Date');
    }
  }

  return {
    oninit: (vnode) => {
      pickerId = _.isEmpty(vnode.attrs.id) ? pickerId += generateID() : vnode.attrs.id;

      if (vnode.attrs.required) {
        required = vnode.attrs.required;
      }

      if (vnode.attrs.readonly) {
        readonly = vnode.attrs.readonly;
      }

      if (vnode.attrs.minDate) {
        minDate = vnode.attrs.minDate;
      }

      if (vnode.attrs.maxDate) {
        maxDate = vnode.attrs.maxDate;
      }

      if (vnode.attrs.minDays) {
        minDays = vnode.attrs.minDays;
      }

      if (vnode.attrs.maxDays) {
        maxDays = vnode.attrs.maxDays;
      }

      if (vnode.attrs.inseparable) {
        inseparable = vnode.attrs.inseparable;
      }


      if (vnode.attrs.isRange) {
        isRange = vnode.attrs.isRange;
        grid = 2;
        calendars = 2;

        if (!_.isEmpty(vnode.attrs.delimiter)) {
          delimiter = vnode.attrs.delimiter
        }
      }

      if (vnode.attrs.grid) {
        grid = vnode.attrs.grid;
      }

      if (vnode.attrs.calendars) {
        calendars = vnode.attrs.calendars;
      }

      if (vnode.attrs.hasResetButton) {
        hasResetButton = vnode.attrs.hasResetButton;
      }

      if (vnode.attrs.autoApply) {
        autoApply = vnode.attrs.autoApply;
      }

      if (!_.isEmpty(vnode.attrs.format)) {
        format = vnode.attrs.format
      }

      if (vnode.attrs.onselect) {
        onselect = vnode.attrs.onselect;
      }

      if (vnode.attrs.onclear) {
        onclear = vnode.attrs.onclear;
      }

      if (vnode.attrs.date) {
        date = vnode.attrs.date;
      }

      if (vnode.attrs.dateStart) {
        dateStart = vnode.attrs.dateStart;
      }

      if (vnode.attrs.dateEnd) {
        dateEnd = vnode.attrs.dateEnd;
      }
    },
    oncreate: (vnode) => {
      initPickerElement();
    },
    onupdate: (vnode) => {
      if (!isRange) {
        date = vnode.attrs.date;
        // picker.setDate(date);
      } else {
        dateStart = vnode.attrs.dateStart;
        dateEnd = vnode.attrs.dateEnd;

        if (_.isEmpty(dateStart) || _.isEmpty(dateEnd)) {
          picker.clear();
        } else {
          picker.setStartDate(dateStart);
          picker.setEndDate(dateEnd);
        }
        if (vnode.attrs.minDate) {
          if (minDate !== vnode.attrs.minDate) {
            minDate = vnode.attrs.minDate;
            // picker.renderAll()
            picker.destroy();
            initPickerElement();
          }
        }
        if (vnode.attrs.maxDays) {
          if (maxDays !== vnode.attrs.maxDays) {
            maxDays = vnode.attrs.maxDays;
            picker.destroy();
            initPickerElement();
          }
        }
      }
    },
    view: (vnode) => {
      return m('.easepick-input-group',
        {
          class: required && isDateEmpty(vnode.attrs.date, vnode.attrs.dateStart, vnode.attrs.dateEnd) ? 'is-invalid-custom' : '',
        },
        m('input.easepick-input', {
          placeholder: getPlaceholder(),
          required: required,
          class: vnode.attrs.class + (required && isDateEmpty(vnode.attrs.date, vnode.attrs.dateStart, vnode.attrs.dateEnd) ? ' is-invalid-custom' : ''),
          id: pickerId,
          onchange: onchange,
          value: renderLabel(vnode.attrs.date, vnode.attrs.dateStart, vnode.attrs.dateEnd),
        }),
        isRange
          ? m('i.bi.bi-calendar-range-fill')
          : m('i.bi.bi-calendar-event-fill'),
      )
    }
  }
}